/**
 * Distance calculation utilities using the Haversine formula.
 */

export interface Coordinates {
  latitude: number;
  longitude: number;
}

const EARTH_RADIUS_KM = 6371;

/**
 * Calculate distance between two coordinates using the Haversine formula.
 * Returns distance in kilometers.
 */
export function haversineDistance(from: Coordinates, to: Coordinates): number {
  const lat1Rad = (from.latitude * Math.PI) / 180;
  const lat2Rad = (to.latitude * Math.PI) / 180;
  const dLatRad = ((to.latitude - from.latitude) * Math.PI) / 180;
  const dLonRad = ((to.longitude - from.longitude) * Math.PI) / 180;

  const a =
    Math.sin(dLatRad / 2) * Math.sin(dLatRad / 2) +
    Math.cos(lat1Rad) *
      Math.cos(lat2Rad) *
      Math.sin(dLonRad / 2) *
      Math.sin(dLonRad / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

/**
 * Calculate distance from user location to item location.
 * Returns formatted distance string or null if location is not available.
 */
export function calculateDistance(
  userLocation: Coordinates | null,
  itemLocation: Coordinates | null
): string | null {
  if (!userLocation || !itemLocation) {
    return null;
  }
  const km = haversineDistance(userLocation, itemLocation);
  return formatDistance(km);
}

/**
 * Format distance for display.
 * - Under 1km: show meters (e.g., "350 m")
 * - 1km+: show kilometers with 1 decimal (e.g., "1.2 km")
 * - 10km+: show kilometers rounded (e.g., "15 km")
 */
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  } else if (km < 10) {
    return `${km.toFixed(1)} km`;
  } else {
    return `${Math.round(km)} km`;
  }
}
